import { useState, useEffect } from "react";
import { Shield, ShieldCheck, Lock, Unlock, Eye, EyeOff, Handshake, FileText, Download, Trash2, Edit, Check, X, AlertTriangle, Info, Clock, Upload } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { useDraftStorage } from "@/hooks/useDraftStorage";
import { sanitizeText } from "@/lib/security/sanitize";

interface ProfileData {
  name: string;
  headline: string;
  skills: string[];
  workHighlights: string[];
}

interface DisclosureData {
  hasRecord: boolean;
  disclosureType: string;
  statement: string;
  files: File[];
  sharingControl: string;
}

interface SharingLog {
  company: string;
  date: string;
  shared: string[];
  status: string;
}

export default function ProgressiveDisclosure() {
  const [profileData] = useState<ProfileData>({
    name: "Your Name",
    headline: "Experienced Professional Seeking Opportunities",
    skills: ["Communication", "Problem Solving", "Leadership"],
    workHighlights: ["5+ years experience", "Proven track record", "Strong team player"],
  });

  const [mutualInterest, setMutualInterest] = useState(false);
  const [requireApproval, setRequireApproval] = useState(true);
  const [interestedCompany, setInterestedCompany] = useState("Tech Solutions Ltd");
  
  // Use secure database storage with auto-save
  const {
    data: savedDisclosure,
    setData: setSavedDisclosure,
    isLoading: isDraftLoading,
    isSaving: isDraftSaving,
  } = useDraftStorage<DisclosureData>({
    draftType: 'disclosure',
    autoSaveInterval: 10000, // 10 seconds
    expiresInDays: 7,
  });

  const [disclosure, setDisclosure] = useState<DisclosureData>({
    hasRecord: false,
    disclosureType: "",
    statement: "",
    files: [],
    sharingControl: "offer",
  });

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [profilePaused, setProfilePaused] = useState(false);

  const [sharingLogs] = useState<SharingLog[]>([]);

  // Load draft on mount
  useEffect(() => {
    if (savedDisclosure) {
      setDisclosure(savedDisclosure);
    }
  }, [savedDisclosure]);

  // Sync disclosure changes to draft storage
  useEffect(() => {
    if (disclosure.hasRecord || disclosure.statement) {
      setSavedDisclosure(disclosure);
    }
  }, [disclosure, setSavedDisclosure]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (disclosure.files.length + files.length > 3) {
      toast.error("Maximum 3 files allowed");
      return;
    }
    
    const validFiles = files.filter(f => f.size <= 5 * 1024 * 1024);
    if (validFiles.length !== files.length) {
      toast.error("Some files exceed 5MB limit");
    }
    
    setDisclosure(prev => ({ ...prev, files: [...prev.files, ...validFiles] }));
  };

  const removeFile = (index: number) => {
    setDisclosure(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const saveDisclosure = async () => {
    // Criminal record data is now encrypted with AES-256-GCM before storage
    // For production: Also send this data to your backend API via HTTPS

    // Sanitize user input before saving
    const sanitizedDisclosure = {
      ...disclosure,
      statement: sanitizeText(disclosure.statement),
    };

    await setSavedDisclosure(sanitizedDisclosure);
    toast.success("Disclosure settings saved securely to database");
  };

  const handleShareInfo = () => {
    setMutualInterest(true);
    setShowApprovalModal(false);
    toast.success(`Information shared with ${interestedCompany}`);
  };

  const handleRevokeAccess = () => {
    setMutualInterest(false);
    setShowRevokeModal(false);
    toast.success("Access revoked successfully");
  };

  const downloadData = () => {
    const data = JSON.stringify({ profile: profileData, disclosure }, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my-data.json";
    a.click();
    toast.success("Data downloaded");
  };

  const deleteDisclosure = () => {
    if (confirm("Are you sure you want to delete your disclosure? This cannot be undone.")) {
      setDisclosure({
        hasRecord: false,
        disclosureType: "",
        statement: "",
        files: [],
        sharingControl: "offer",
      });
      sessionStorage.removeItem("disclosure_data");
      sessionStorage.removeItem("disclosure_draft");
      toast.success("Disclosure deleted");

      // TODO: Also delete from backend server
      // Example: await api.deleteDisclosure();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container max-w-[1000px] mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Progressive Disclosure</h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-6">
            Share your story on your terms. Criminal record information is only shared when there's mutual interest, protecting your privacy.
          </p>
          <div className="flex justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-green-500" />
              <span>Privacy Protected</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-500" />
              <span>Encrypted Storage</span>
            </div>
            <div className="flex items-center gap-2">
              <EyeOff className="w-5 h-5 text-indigo-500" />
              <span>You Control Visibility</span>
            </div>
          </div>
        </div>

        {/* Three Panels */}
        <div className="space-y-6 mb-24">
          {/* Panel 1: Public Profile */}
          <Card className="border-l-4 border-green-500 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Eye className="w-6 h-6 text-green-500" />
                  <CardTitle>What Employers See First</CardTitle>
                </div>
                <Badge className="bg-green-500">VISIBLE TO ALL</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{profileData.name}</h3>
                <p className="text-muted-foreground">{profileData.headline}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium mb-2">Key Skills</p>
                <div className="flex flex-wrap gap-2">
                  {profileData.skills.map((skill) => (
                    <Badge key={skill} variant="secondary">{skill}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Work Experience Highlights</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {profileData.workHighlights.map((highlight, i) => (
                    <li key={i}>• {highlight}</li>
                  ))}
                </ul>
              </div>

              <Badge className="bg-blue-500">Interested in opportunities</Badge>
              
              <Button variant="outline" size="sm" className="mt-2">
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            </CardContent>
          </Card>

          {/* Panel 2: After Mutual Interest */}
          <Card className="border-l-4 border-amber-500 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Handshake className="w-6 h-6 text-amber-500" />
                  <CardTitle>Shared When Employer Shows Interest</CardTitle>
                </div>
                <Badge className="bg-amber-500">CONTROLLED SHARING</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {!mutualInterest ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Lock className="w-5 h-5" />
                    <span className="font-medium">Protected Information</span>
                  </div>
                  
                  <div className="blur-sm pointer-events-none select-none">
                    <ul className="space-y-2 text-sm">
                      <li>• Full work history with details</li>
                      <li>• Professional references</li>
                      <li>• Complete education background</li>
                      <li>• Availability and start date</li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <Info className="w-5 h-5 text-blue-600 mb-2" />
                    <p className="text-sm text-blue-900">
                      This information is automatically shared when an employer requests to connect with you. You'll be notified first.
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <Label htmlFor="approval" className="text-sm">Require my approval before sharing</Label>
                    <Switch
                      id="approval"
                      checked={requireApproval}
                      onCheckedChange={setRequireApproval}
                    />
                  </div>

                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setShowApprovalModal(true)}
                  >
                    Simulate Employer Interest
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-green-600">
                    <Unlock className="w-5 h-5" />
                    <span className="font-medium">Information Unlocked</span>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-900 font-medium">
                      Shared with {interestedCompany} on {new Date().toLocaleDateString()}
                    </p>
                  </div>

                  <ul className="space-y-2 text-sm">
                    <li>• Full work history with details</li>
                    <li>• Professional references</li>
                    <li>• Complete education background</li>
                    <li>• Availability and start date</li>
                  </ul>

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setShowRevokeModal(true)}>
                      Revoke Access
                    </Button>
                    <Button variant="outline" size="sm">
                      View Their Profile
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Panel 3: Criminal Record Disclosure */}
          <Card className="border-l-4 border-red-500 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-red-500" />
                  <CardTitle>Criminal Record Information (Maximum Privacy)</CardTitle>
                </div>
                <Badge className="bg-red-500">MAXIMUM PRIVACY</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="disclosure" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="disclosure">Your Disclosure</TabsTrigger>
                  <TabsTrigger value="guidance">Guidance</TabsTrigger>
                  <TabsTrigger value="log">Sharing Log</TabsTrigger>
                </TabsList>

                {/* Tab 1: Your Disclosure */}
                <TabsContent value="disclosure" className="space-y-6 mt-6">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="has-record" className="text-base">I have a criminal record to disclose</Label>
                    <Switch
                      id="has-record"
                      checked={disclosure.hasRecord}
                      onCheckedChange={(checked) => setDisclosure(prev => ({ ...prev, hasRecord: checked }))}
                    />
                  </div>

                  {!disclosure.hasRecord ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                      <Check className="w-12 h-12 text-green-500 mx-auto mb-2" />
                      <p className="text-green-900 font-medium">No disclosure needed</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div>
                        <Label className="text-base mb-3 block">Disclosure Type</Label>
                        <RadioGroup
                          value={disclosure.disclosureType}
                          onValueChange={(value) => setDisclosure(prev => ({ ...prev, disclosureType: value }))}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="spent" id="spent" />
                            <Label htmlFor="spent">Spent conviction (ROA protected)</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="unspent" id="unspent" />
                            <Label htmlFor="unspent">Unspent conviction</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="caution" id="caution" />
                            <Label htmlFor="caution">Caution/warning</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="prefer-not" id="prefer-not" />
                            <Label htmlFor="prefer-not">Prefer not to say</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      <div>
                        <Label htmlFor="statement" className="text-base mb-2 block">Your Statement</Label>
                        <Textarea
                          id="statement"
                          placeholder="Explain what happened, what you learned, and how you've moved forward..."
                          value={disclosure.statement}
                          onChange={(e) => {
                            // Sanitize input as user types
                            const sanitized = sanitizeText(e.target.value);
                            setDisclosure(prev => ({ ...prev, statement: sanitized }));
                          }}
                          maxLength={500}
                          rows={6}
                          className="resize-none"
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          {disclosure.statement.length}/500 characters
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Focus on rehabilitation, personal growth, and what you've done since.
                        </p>
                      </div>

                      <div>
                        <Label className="text-base mb-2 block">Supporting Documents</Label>
                        <div className="border-2 border-dashed rounded-lg p-6 text-center">
                          <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mb-2">
                            Add character reference, training certificates, or rehabilitation evidence
                          </p>
                          <p className="text-xs text-muted-foreground mb-4">
                            Max 3 files, 5MB each
                          </p>
                          <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="file-upload"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById("file-upload")?.click()}
                          >
                            Choose Files
                          </Button>
                        </div>
                        
                        {disclosure.files.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {disclosure.files.map((file, index) => (
                              <div key={index} className="flex items-center justify-between bg-secondary p-2 rounded">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4" />
                                  <span className="text-sm">{file.name}</span>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFile(index)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="sharing" className="text-base mb-2 block">Sharing Control</Label>
                        <select
                          id="sharing"
                          value={disclosure.sharingControl}
                          onChange={(e) => setDisclosure(prev => ({ ...prev, sharingControl: e.target.value }))}
                          className="w-full p-2 border rounded-md bg-background"
                        >
                          <option value="offer">Only share after job offer discussion</option>
                          <option value="interview">Share after first interview</option>
                          <option value="request">Share when employer specifically asks</option>
                          <option value="never">Never share automatically</option>
                        </select>
                      </div>

                      <Button onClick={() => void saveDisclosure()} className="w-full">
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Save Disclosure Securely
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Tab 2: Guidance */}
                <TabsContent value="guidance" className="space-y-4 mt-6">
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-secondary rounded-lg hover:bg-secondary/80">
                      <span className="font-medium">What is ROA (Rehabilitation of Offenders Act)?</span>
                      <Info className="w-5 h-5" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-4 border border-t-0 rounded-b-lg">
                      <p className="text-sm mb-3">
                        The Rehabilitation of Offenders Act 1974 helps people with criminal convictions by allowing certain old convictions to become "spent" after a set period of time. Once spent, you generally don't have to disclose them to employers.
                      </p>
                      <div className="bg-blue-50 p-3 rounded">
                        <p className="text-sm font-medium mb-2">Spent vs Unspent</p>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Spent:</strong> Old convictions you usually don't have to disclose</li>
                          <li>• <strong>Unspent:</strong> Recent convictions that must be disclosed if asked</li>
                        </ul>
                      </div>
                      <a href="https://www.gov.uk/government/publications/new-guidance-on-the-rehabilitation-of-offenders-act-1974" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
                        Read official guidance on gov.uk →
                      </a>
                    </CollapsibleContent>
                  </Collapsible>

                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-secondary rounded-lg hover:bg-secondary/80">
                      <span className="font-medium">Do I have to disclose?</span>
                      <Info className="w-5 h-5" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-4 border border-t-0 rounded-b-lg">
                      <div className="bg-amber-50 border border-amber-200 p-3 rounded mb-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mb-2" />
                        <p className="text-sm text-amber-900 font-medium">
                          This is guidance, not legal advice. Check with a professional if unsure.
                        </p>
                      </div>
                      <p className="text-sm mb-2">It depends on:</p>
                      <ul className="text-sm space-y-1 mb-3">
                        <li>• Whether your conviction is spent or unspent</li>
                        <li>• The type of job (some roles require enhanced DBS checks)</li>
                        <li>• Whether the employer specifically asks</li>
                      </ul>
                      <p className="text-sm">
                        Generally, if your conviction is spent and the job doesn't require a DBS check, you don't have to disclose it.
                      </p>
                    </CollapsibleContent>
                  </Collapsible>

                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-secondary rounded-lg hover:bg-secondary/80">
                      <span className="font-medium">How to write your statement</span>
                      <Info className="w-5 h-5" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-4 border border-t-0 rounded-b-lg">
                      <p className="text-sm font-medium mb-2">Tips:</p>
                      <ul className="text-sm space-y-1 mb-4">
                        <li>✓ Be honest but brief</li>
                        <li>✓ Focus on what you've learned</li>
                        <li>✓ Highlight positive changes since</li>
                        <li>✓ Show accountability without being apologetic</li>
                        <li>✓ Mention rehabilitation activities</li>
                      </ul>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="bg-red-50 p-3 rounded">
                          <p className="text-sm font-medium text-red-900 mb-1">❌ Avoid</p>
                          <p className="text-xs text-red-800">
                            "I made a terrible mistake and I'm so sorry. It was stupid and I regret it every day."
                          </p>
                        </div>
                        <div className="bg-green-50 p-3 rounded">
                          <p className="text-sm font-medium text-green-900 mb-1">✓ Better</p>
                          <p className="text-xs text-green-800">
                            "In 2018, I was convicted of theft. Since then, I've completed a rehabilitation program, volunteered with youth mentoring, and rebuilt my life. I'm now focused on contributing positively to society through stable employment."
                          </p>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-secondary rounded-lg hover:bg-secondary/80">
                      <span className="font-medium">Your rights</span>
                      <Info className="w-5 h-5" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-4 border border-t-0 rounded-b-lg">
                      <ul className="text-sm space-y-2">
                        <li>• Employers cannot discriminate based on spent convictions</li>
                        <li>• They can only ask about criminal records if relevant to the role</li>
                        <li>• You have the right to challenge unfair rejection</li>
                        <li>• Employers must assess your suitability fairly, not automatically reject</li>
                        <li>• You can request feedback if rejected due to your record</li>
                      </ul>
                      <a href="https://www.unlock.org.uk" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline mt-3 inline-block">
                        Visit Unlock charity for detailed guidance →
                      </a>
                    </CollapsibleContent>
                  </Collapsible>
                </TabsContent>

                {/* Tab 3: Sharing Log */}
                <TabsContent value="log" className="mt-6">
                  {sharingLogs.length === 0 ? (
                    <div className="text-center py-12">
                      <ShieldCheck className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        No information shared yet. You're in control.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2 text-sm font-medium">Company</th>
                            <th className="text-left p-2 text-sm font-medium">Date</th>
                            <th className="text-left p-2 text-sm font-medium">Shared</th>
                            <th className="text-left p-2 text-sm font-medium">Status</th>
                            <th className="text-left p-2 text-sm font-medium">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sharingLogs.map((log, i) => (
                            <tr key={i} className="border-b">
                              <td className="p-2 text-sm">{log.company}</td>
                              <td className="p-2 text-sm">{log.date}</td>
                              <td className="p-2">
                                <div className="flex gap-1">
                                  {log.shared.includes("profile") && <Eye className="w-4 h-4" />}
                                  {log.shared.includes("details") && <Handshake className="w-4 h-4" />}
                                  {log.shared.includes("disclosure") && <Shield className="w-4 h-4" />}
                                </div>
                              </td>
                              <td className="p-2">
                                <Badge variant="secondary" className="text-xs">{log.status}</Badge>
                              </td>
                              <td className="p-2">
                                <Button variant="ghost" size="sm">Revoke</Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Privacy Controls Footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-background border-t shadow-lg">
          <div className="container max-w-[1000px] mx-auto px-4 py-4">
            <div className="flex flex-wrap gap-3 justify-center">
              <Button variant="outline" onClick={() => setShowSettingsModal(true)}>
                <Lock className="w-4 h-4 mr-2" />
                Visibility Settings
              </Button>
              <Button variant="outline" onClick={downloadData}>
                <Download className="w-4 h-4 mr-2" />
                Download My Data
              </Button>
              <Button variant="outline" onClick={deleteDisclosure}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Disclosure
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Approval Modal */}
      <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Employer Interested in Your Profile</DialogTitle>
            <DialogDescription>
              An employer would like to connect with you and view your protected information.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                    TS
                  </div>
                  <div>
                    <p className="font-semibold">{interestedCompany}</p>
                    <p className="text-sm text-muted-foreground">Technology • Senior Developer</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div>
              <p className="text-sm font-medium mb-2">Will be shared:</p>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>✓ Full work history</li>
                <li>✓ Professional references</li>
                <li>✓ Education details</li>
                <li>✓ Availability</li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                Respond within 48 hours or request expires
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button onClick={handleShareInfo} className="w-full bg-green-500 hover:bg-green-600">
              Share Information
            </Button>
            <Button variant="secondary" onClick={() => setShowApprovalModal(false)} className="w-full">
              Not Interested
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Access Modal */}
      <Dialog open={showRevokeModal} onOpenChange={setShowRevokeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Revoke Access?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke access? {interestedCompany} will no longer see your protected information. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevokeModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevokeAccess}>
              Yes, Revoke Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visibility Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Visibility Settings</DialogTitle>
            <DialogDescription>
              Control who can see your profile and when
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="pause-profile" className="text-base">Pause my profile</Label>
                <p className="text-sm text-muted-foreground">Make your profile completely private</p>
              </div>
              <Switch
                id="pause-profile"
                checked={profilePaused}
                onCheckedChange={setProfilePaused}
              />
            </div>

            <div>
              <Label className="text-base mb-3 block">Who can see you</Label>
              <RadioGroup defaultValue="all">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="all" />
                  <Label htmlFor="all">All employers</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="industries" id="industries" />
                  <Label htmlFor="industries">Selected industries only</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="applied" id="applied" />
                  <Label htmlFor="applied">Companies I apply to</Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label className="text-base mb-2 block">Block list</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Add companies who should never see your profile
              </p>
              <Button variant="outline" size="sm">
                <X className="w-4 h-4 mr-2" />
                Manage Block List
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => {
              setShowSettingsModal(false);
              toast.success("Settings saved");
            }}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
